import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/apiClient';
import { clearValidationLabResults } from '../utils/validationLabResultsSession';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const sidebarLinkClassName = ({ isActive }) => (
  `dashboard-sidebar-link${isActive ? ' dashboard-sidebar-link-active' : ''}`
);

function AuthenticatedAppLayout({ setIsAuthenticated }) {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchUserInfo = async () => {
      try {
        const response = await apiFetch(`${API_BASE_URL}/me`, {
          method: 'GET',
        });

        if (!response.ok) {
          throw new Error('Failed to fetch user info');
        }

        const data = await response.json();
        if (!cancelled) {
          setUserInfo(data);
        }
      } catch (error) {
        console.error('Failed to fetch sidebar user info:', error);
      }
    };

    fetchUserInfo();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogout = async () => {
    clearValidationLabResults(userInfo?.id ?? userInfo?.user_id);
    try {
      await apiFetch(`${API_BASE_URL}/logout`, {
        method: 'POST',
      });
    } catch (error) {
      // Local sign-out still completes when the API is temporarily unavailable.
    }
    setIsAuthenticated(false);
    navigate('/');
  };

  return (
    <div className="dashboard-page authenticated-app-shell">
      <aside className="dashboard-sidebar">
        <div className="dashboard-sidebar-header">
          <Link to="/" aria-label="Black Peak Analytics home">
            <img
              src="/assets/black_peak_analytics_logo_transparent_240.png"
              alt="Black Peak Analytics"
              className="dashboard-sidebar-logo"
            />
          </Link>
        </div>

        <nav className="dashboard-sidebar-nav" aria-label="Authenticated application navigation">
          <NavLink to="/dashboard" end className={sidebarLinkClassName}>
            <svg className="dashboard-sidebar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7"></rect>
              <rect x="14" y="3" width="7" height="7"></rect>
              <rect x="14" y="14" width="7" height="7"></rect>
              <rect x="3" y="14" width="7" height="7"></rect>
            </svg>
            <span className="dashboard-sidebar-text">Dashboard</span>
          </NavLink>
          <NavLink to="/scan" className={sidebarLinkClassName}>
            <svg className="dashboard-sidebar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <span className="dashboard-sidebar-text">Run Scan</span>
          </NavLink>
          <NavLink to="/stock" className={sidebarLinkClassName}>
            <svg className="dashboard-sidebar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3v18h18"></path>
              <path d="M7 14l4-4 3 3 5-6"></path>
            </svg>
            <span className="dashboard-sidebar-text">Stock Research</span>
          </NavLink>
          <NavLink to="/portfolios" className={sidebarLinkClassName}>
            <svg className="dashboard-sidebar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
            </svg>
            <span className="dashboard-sidebar-text">Portfolios</span>
          </NavLink>
          <NavLink to="/alerts" className={sidebarLinkClassName}>
            <svg className="dashboard-sidebar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
            </svg>
            <span className="dashboard-sidebar-text">Alerts</span>
          </NavLink>
          <NavLink to="/validation-lab" className={sidebarLinkClassName}>
            <svg className="dashboard-sidebar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 11a3 3 0 1 0 6 0a3 3 0 0 0 -6 0"></path>
              <path d="M17.657 16.657l-4.243 4.243a2 2 0 0 1 -2.827 0l-4.244 -4.243a8 8 0 1 1 11.314 0z"></path>
            </svg>
            <span className="dashboard-sidebar-text">Strategy Lab</span>
          </NavLink>
          <NavLink to="/methodology" className={sidebarLinkClassName}>
            <svg className="dashboard-sidebar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4h16v16H4z"></path>
              <path d="M8 8h8M8 12h8M8 16h5"></path>
            </svg>
            <span className="dashboard-sidebar-text">Methodology</span>
          </NavLink>
          <NavLink to="/account" className={sidebarLinkClassName}>
            <svg className="dashboard-sidebar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"></path>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
            </svg>
            <span className="dashboard-sidebar-text">Account</span>
          </NavLink>
          {userInfo?.is_admin && (
            <>
              <NavLink to="/admin/account-management" className={sidebarLinkClassName}>
                <svg className="dashboard-sidebar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M19 8v6M22 11h-6"></path>
                </svg>
                <span className="dashboard-sidebar-text">Account Management</span>
              </NavLink>
              <NavLink to="/admin/data-quality" className={sidebarLinkClassName}>
                <svg className="dashboard-sidebar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3h18v18H3z"></path>
                  <path d="M7 12h10"></path>
                  <path d="M7 8h10"></path>
                  <path d="M7 16h6"></path>
                </svg>
                <span className="dashboard-sidebar-text">Data Quality Issues</span>
              </NavLink>
              <NavLink to="/admin/client-analytics" className={sidebarLinkClassName}>
                <svg className="dashboard-sidebar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18"></path>
                  <path d="M7 14l4-4 3 3 5-6"></path>
                </svg>
                <span className="dashboard-sidebar-text">Client Analytics</span>
              </NavLink>
            </>
          )}
        </nav>

        <div className="dashboard-sidebar-footer">
          <div className="dashboard-user-profile">
            <div className="dashboard-user-avatar">
              {userInfo?.email ? userInfo.email.substring(0, 2).toUpperCase() : 'U'}
            </div>
            <div className="dashboard-user-info">
              <div className="dashboard-user-name">
                {userInfo?.email?.split('@')[0] || 'User'}
              </div>
              <div className="dashboard-user-plan">
                {userInfo?.plan_type === 'unlimited' ? 'Premium' : userInfo?.plan_type === 'pro' ? 'Pro' : 'Free'}
              </div>
            </div>
            <button onClick={handleLogout} className="dashboard-user-logout" title="Logout">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
            </button>
          </div>
        </div>
      </aside>

      <main className="dashboard-main authenticated-app-main">
        <Outlet />
      </main>
    </div>
  );
}

export default AuthenticatedAppLayout;
